import axios, { AxiosInstance } from 'axios';
import { Reservation, HospitableConfig } from '../types/hospitable';
import { API_BASE_URL } from '../config';

class HospitableService {
  private api: AxiosInstance;

  constructor(config: HospitableConfig) {
    // Utiliser l'API endpoint (Netlify Functions ou autre)
    const baseUrl = config.baseUrl || API_BASE_URL;
    
    this.api = axios.create({
      baseURL: baseUrl,
      headers: {
        'Authorization': `Bearer ${config.apiKey}`,
        'Content-Type': 'application/json',
      },
    });
  }

  async getReservations(startDate?: string, endDate?: string): Promise<Reservation[]> {
    try {
      const params: any = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      console.log('Fetching reservations from:', this.api.defaults.baseURL + '/reservations');
      const response = await this.api.get('/reservations', { params });
      console.log('Response received:', response.data);
      
      // L'API Hospitable peut retourner les données dans différents formats
      let reservations = [];
      
      // Vérifier si c'est une erreur
      if (Array.isArray(response.data) && response.data[0] === "Not found") {
        console.error('API returned "Not found"');
        throw new Error('Clé API invalide ou expirée. Veuillez vérifier votre clé API.');
      }
      
      // Vérifier si c'est une erreur d'authentification
      if (response.data && response.data.error) {
        console.error('API error:', response.data);
        throw new Error(response.data.message || 'Erreur API');
      }
      
      if (Array.isArray(response.data)) {
        reservations = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        reservations = response.data.data;
      } else if (response.data && response.data.reservations) {
        reservations = response.data.reservations;
      } else if (response.data && response.data.data) {
        // Nouveau format de l'API v2
        reservations = response.data.data;
      }
      
      // Mapper les données au format attendu (structure API Hospitable v2)
      return reservations.map((res: any) => {
        // Construire le nom complet du guest
        let guestName = 'Guest';
        if (res.guest) {
          if (res.guest.full_name) {
            guestName = res.guest.full_name;
          } else if (res.guest.first_name || res.guest.last_name) {
            guestName = `${res.guest.first_name || ''} ${res.guest.last_name || ''}`.trim();
          }
        } else if (res.guest_name) {
          guestName = res.guest_name;
        }

        // Obtenir le nom de la propriété
        let propertyName = 'Property';
        if (res.property) {
          propertyName = res.property.public_name || res.property.name || res.property.nickname || 'Property';
        } else if (res.listing?.name) {
          propertyName = res.listing.name;
        }

        // Obtenir le téléphone du guest
        let guestPhone = '';
        if (res.guest?.phone_numbers && res.guest.phone_numbers.length > 0) {
          guestPhone = res.guest.phone_numbers[0];
        } else if (res.guest?.phone) {
          guestPhone = res.guest.phone;
        } else if (res.guest_phone) {
          guestPhone = res.guest_phone;
        }

        return {
          id: res.id || '',
          guest_name: guestName,
          property_name: propertyName,
          check_in: res.arrival_date || res.check_in || '',
          check_out: res.departure_date || res.check_out || '',
          // Le montant est en centimes dans financials.guest.total_price.amount
          total_amount: res.financials?.guest?.total_price?.amount ? res.financials.guest.total_price.amount / 100 : 
                       res.total_price || res.price || 0,
          status: res.reservation_status?.current?.category || res.status || 'confirmed',
          nights: res.nights || 1,
          guest_email: res.guest?.email || res.guest_email || '',
          guest_phone: guestPhone,
          confirmation_code: res.code || res.confirmation_code || '',
          created_at: res.booking_date || res.created_at || new Date().toISOString(),
          // Détails financiers
          accommodation_amount: res.financials?.guest?.accommodation?.amount ? res.financials.guest.accommodation.amount / 100 : undefined,
          fees: res.financials?.guest?.fees?.map((fee: any) => ({
            amount: fee.amount / 100,
            label: fee.label
          })) || [],
          taxes: res.financials?.guest?.taxes?.map((tax: any) => ({
            amount: tax.amount / 100,
            label: tax.label
          })) || [],
          discounts: res.financials?.guest?.discounts?.map((discount: any) => ({
            amount: Math.abs(discount.amount / 100), // Prendre la valeur absolue pour afficher positivement
            label: discount.label
          })) || []
        };
      });
    } catch (error) {
      console.error('Error fetching reservations:', error);
      throw error;
    }
  }

  async getReservationById(id: string): Promise<Reservation> {
    try {
      const response = await this.api.get(`/reservations/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching reservation:', error);
      throw error;
    }
  }
}

export default HospitableService;